import DOMPurify from 'dompurify';
import HTMLReactParser from 'html-react-parser';

import { SafeHtmlProps } from './safeHtml.types';

const SHOP_RICH_TEXT_SANITIZE_CONFIG = {
  ALLOW_ARIA_ATTR: false,
  ALLOW_DATA_ATTR: false,
  ALLOWED_ATTR: ['href'],
  ALLOWED_TAGS: [
    'a',
    'blockquote',
    'br',
    'em',
    'h2',
    'h3',
    'h4',
    'li',
    'ol',
    'p',
    'strong',
    'u',
    'ul'
  ],
  ALLOWED_URI_REGEXP: /^(?:https:\/\/|mailto:)/i,
  FORBID_ATTR: ['ping', 'srcset', 'style']
};

// DOMPurify v3 checks `target` against ALLOWED_URI_REGEXP — `_blank` fails the
// regexp and gets stripped even when listed in ALLOWED_ATTR. Pre-parse to record
// which anchor INDICES had target="_blank", then restore only those after
// sanitization. Index-keyed (not href-keyed) so two links to the same URL with
// different targets are handled independently.
const sanitizeShopRichText = (html: string): string => {
  const pre = new DOMParser().parseFromString(html, 'text/html');
  const blankIndices = new Set<number>();
  pre.querySelectorAll('a').forEach((a, i) => {
    if (a.getAttribute('target') === '_blank') blankIndices.add(i);
  });
  const sanitized = DOMPurify.sanitize(html, SHOP_RICH_TEXT_SANITIZE_CONFIG);
  if (!blankIndices.size) return sanitized;
  const post = new DOMParser().parseFromString(sanitized, 'text/html');
  post.querySelectorAll('a').forEach((a, i) => {
    if (blankIndices.has(i)) {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
    }
  });
  return post.body.innerHTML;
};

/**
 * Admin-authored signable documents (LMD-2992). Mirrors the tag and attribute allow-list of the
 * API's `sanitizeSignableDocumentHtml`, which is the primary gate and runs on write.
 *
 * `class` and `style` are both allowed deliberately: documents are styled with Tailwind utility
 * classes, and inline `style` is permitted by product decision because the consents authored
 * before this feature carry it. Stripping either changes how live documents render — which is
 * exactly the bug this component's `lifemd-legal` sibling used to have.
 *
 * This is a second gate on *tags and attributes*, not a replacement for the server's pass.
 * DOMPurify has no CSS-property allow-list, so it cannot reproduce the server's rule that a
 * `style` attribute may not carry `position`, `opacity`, `display` and the rest of the concealment
 * set. The API applies that on write only, so a document authored before it landed can still carry
 * a concealing inline style until it is next saved.
 *
 * `ADD_URI_SAFE_ATTR: ['target']` is needed because DOMPurify v3 validates `target` against
 * `ALLOWED_URI_REGEXP` and would otherwise strip `_blank` — the same trap `sanitizeShopRichText`
 * above works around by hand.
 */
const SIGNABLE_DOCUMENT_SANITIZE_CONFIG = {
  ADD_URI_SAFE_ATTR: ['target'],
  ALLOW_ARIA_ATTR: true,
  ALLOW_DATA_ATTR: true,
  ALLOWED_ATTR: [
    'class',
    'colspan',
    'dir',
    'href',
    'id',
    'lang',
    'rel',
    'reversed',
    'rowspan',
    'scope',
    'start',
    'style',
    'summary',
    'target',
    'title',
    'type',
    'value'
  ],
  ALLOWED_TAGS: [
    'a',
    'b',
    'blockquote',
    'br',
    'caption',
    'div',
    'em',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'hr',
    'i',
    'li',
    'ol',
    'p',
    's',
    'small',
    'span',
    'strong',
    'sub',
    'sup',
    'table',
    'tbody',
    'td',
    'tfoot',
    'th',
    'thead',
    'tr',
    'u',
    'ul'
  ],
  /**
   * Matches what the API's `allowedSchemes: ['https', 'mailto', 'tel']` actually permits.
   *
   * sanitize-html only applies that list to hrefs that *have* a scheme, so the server keeps
   * `#anchor` and `/legal/…` — this regex was dropping both, and `id` is allowed above, so a
   * document with an in-page table of contents ended up with ids and dead links.
   *
   * `//evil.com` is excluded by the negative lookahead: it is protocol-relative, so it leaves the
   * portal despite looking like a path.
   */
  ALLOWED_URI_REGEXP: /^(?:https:\/\/|mailto:|tel:|#|\/(?!\/))/i,
  FORBID_TAGS: ['base', 'embed', 'form', 'iframe', 'input', 'link', 'object', 'script', 'style']
};

// Mirrors the API's `transformTags: { a: simpleTransform('a', { rel: 'noopener noreferrer' }) }`,
// which rewrites every anchor on write. Repeated here because the API only sanitizes on write, so
// a document authored before that pass landed still reaches this component with a bare
// `target="_blank"` and a live `window.opener` handle back to the portal.
const detachAnchors = (sanitized: string): string => {
  if (!sanitized.includes('<a')) return sanitized;

  const parsed = new DOMParser().parseFromString(sanitized, 'text/html');
  parsed
    .querySelectorAll('a')
    .forEach((anchor) => anchor.setAttribute('rel', 'noopener noreferrer'));

  return parsed.body.innerHTML;
};

const sanitizeFor = (html: string, policy: NonNullable<SafeHtmlProps['policy']>): string => {
  switch (policy) {
    case 'shopRichText':
      return sanitizeShopRichText(html);
    case 'signableDocument':
      return detachAnchors(DOMPurify.sanitize(html, SIGNABLE_DOCUMENT_SANITIZE_CONFIG));
    default:
      return DOMPurify.sanitize(html);
  }
};

const SafeHtml: React.FC<SafeHtmlProps> = ({ html, policy = 'default' }) => {
  const sanitized = sanitizeFor(html, policy);
  // eslint-disable-next-line no-restricted-syntax
  const parsed = HTMLReactParser(sanitized);

  return <>{parsed}</>;
};

export default SafeHtml;
